import { useCallback, useEffect, useMemo, useState } from 'react';

import { CreateUserDialog } from '../components/CreateUserDialog';
import { SparkInput } from '../components/SparkInput';
import { SparkMessage } from '../components/SparkMessage';
import { useAuth } from '../context/AuthContext';
import { ApiError, deleteUser, listUsers, resetUserMfa, resetUserPassword, updateUser } from '../lib/api';
import type { Role, User, UserResponse } from '../lib/types';

/** Root and administrator view for provisioning and maintaining accounts. */
export function UsersPage() {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [credential, setCredential] = useState<{ userId: string; password: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const isRoot = currentUser?.role === 'root';
  const assignableRoles = useMemo<Role[]>(() => (isRoot ? ['agent', 'admin'] : ['agent']), [isRoot]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const result = await listUsers(
          { search, role: roleFilter, status: statusFilter, limit: 100 },
          signal,
        );
        setUsers(result.users);
        setTotal(result.total);
        setError('');
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof ApiError ? err.message : 'The user list could not be loaded.');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [search, roleFilter, statusFilter],
  );

  // Debounce so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void load(controller.signal);
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  const canManage = (target: User) => {
    if (!currentUser) return false;
    if (target.role === 'root') return false;
    if (isRoot) return true;
    return target.role === 'agent';
  };

  const handleCreated = (result: UserResponse) => {
    setDialogOpen(false);
    setNotice(`User ${result.user.userId} was created.`);
    if (result.temporaryPassword) {
      setCredential({ userId: result.user.userId, password: result.temporaryPassword });
    }
    void load();
  };

  const handleToggleStatus = async (target: User) => {
    setBusyId(target.id);
    setError('');
    try {
      const next = target.status === 'active' ? 'disabled' : 'active';
      await updateUser(target.id, { status: next });
      setNotice(`User ${target.userId} was ${next === 'active' ? 'enabled' : 'disabled'}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The user could not be updated.');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleTicketing = async (target: User) => {
    const next = !target.ticketing;
    const question = next
      ? `Allow ${target.userId} to issue tickets?`
      : `Revoke ticketing from ${target.userId}? W* will answer NOTKTS for them.`;
    if (!window.confirm(question)) return;
    setBusyId(target.id);
    setError('');
    try {
      await updateUser(target.id, { ticketing: next });
      setNotice(`Ticketing ${next ? 'allowed for' : 'revoked from'} ${target.userId}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The user could not be updated.');
    } finally {
      setBusyId(null);
    }
  };

  const handleResetPassword = async (target: User) => {
    if (!window.confirm(`Issue a new temporary password for ${target.userId}? Their active sessions will end.`)) {
      return;
    }
    setBusyId(target.id);
    setError('');
    try {
      const result = await resetUserPassword(target.id);
      setNotice(`A temporary password was issued for ${target.userId}.`);
      if (result.temporaryPassword) {
        setCredential({ userId: target.userId, password: result.temporaryPassword });
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The password could not be reset.');
    } finally {
      setBusyId(null);
    }
  };

  const handleResetMfa = async (target: User) => {
    if (
      !window.confirm(
        `Turn off two-step verification for ${target.userId}? Use this when they lost their phone. They will be signed out and can set it up again.`,
      )
    ) {
      return;
    }
    setBusyId(target.id);
    setError('');
    try {
      await resetUserMfa(target.id);
      setNotice(`Two-step verification was reset for ${target.userId}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Two-step verification could not be reset.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (target: User) => {
    if (!window.confirm(`Delete ${target.userId}? This cannot be undone.`)) return;
    setBusyId(target.id);
    setError('');
    try {
      await deleteUser(target.id);
      setNotice(`User ${target.userId} was deleted.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The user could not be deleted.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Users</h1>
          <p className="page-header__subtitle">
            {total} account{total === 1 ? '' : 's'} in this agency.{' '}
            {isRoot ? 'You can create administrators and agents.' : 'You can create agent accounts.'}
          </p>
        </div>
        <button type="button" className="spark-btn spark-btn--md spark-btn--primary" onClick={() => setDialogOpen(true)}>
          Create user
        </button>
      </div>

      {error && <SparkMessage variant="error">{error}</SparkMessage>}
      {notice && !credential && <SparkMessage variant="success">{notice}</SparkMessage>}

      {credential && (
        <div className="credential-callout">
          <strong>Temporary password for {credential.userId}</strong>
          <div className="credential-callout__value">
            <span className="mono">{credential.password}</span>
            <button
              type="button"
              className="spark-btn spark-btn--md spark-btn--secondary"
              onClick={() => navigator.clipboard?.writeText(credential.password)}
            >
              Copy
            </button>
            <button
              type="button"
              className="spark-btn spark-btn--md spark-btn--secondary"
              onClick={() => setCredential(null)}
            >
              Dismiss
            </button>
          </div>
          <p className="field-hint" style={{ margin: '10px 0 0' }}>
            This is shown once. Share it with the user over a trusted channel; they must change it at first sign-in.
          </p>
        </div>
      )}

      <div className="toolbar">
        <SparkInput
          label="Search"
          placeholder="Username, name, email or PCC"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select className="select" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
          <option value="">All roles</option>
          <option value="root">Root</option>
          <option value="admin">Administrator</option>
          <option value="agent">Agent</option>
        </select>
        <select className="select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Email</th>
              <th>PCC</th>
              <th>Role</th>
              <th>Status</th>
              <th>Ticketing</th>
              <th>Last sign-in</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 && (
              <tr>
                <td colSpan={9} className="table__empty">
                  Loading users…
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={9} className="table__empty">
                  No users match the current filters.
                </td>
              </tr>
            )}
            {users.map((row) => (
              <tr key={row.id}>
                <td className="mono">{row.userId}</td>
                <td>{`${row.firstName} ${row.lastName}`.trim() || '—'}</td>
                <td>{row.email || '—'}</td>
                <td className="mono">{row.pcc || '—'}</td>
                <td>
                  <span className={`badge badge--${row.role}`}>{row.role}</span>
                </td>
                <td>
                  <span className={`badge badge--${row.status}`}>{row.status}</span>
                  {row.twoFactorEnabled && (
                    <span className="field-hint" style={{ margin: '4px 0 0', display: 'block' }}>
                      2-step on
                    </span>
                  )}
                  {row.mustChangePassword && (
                    <span className="field-hint" style={{ margin: '4px 0 0', display: 'block' }}>
                      password change pending
                    </span>
                  )}
                </td>
                <td>
                  {row.role === 'root' || row.ticketing ? (
                    <span className="badge badge--active">Allowed</span>
                  ) : (
                    <span className="badge badge--disabled">NOTKTS</span>
                  )}
                </td>
                <td>{formatDate(row.lastLoginAt)}</td>
                <td>
                  <div className="table__actions">
                    {canManage(row) && (
                      <>
                        <button
                          type="button"
                          className="spark-btn-link"
                          disabled={busyId === row.id}
                          onClick={() => void handleResetPassword(row)}
                        >
                          Reset password
                        </button>
                        {row.twoFactorEnabled && (
                          <button
                            type="button"
                            className="spark-btn-link"
                            disabled={busyId === row.id}
                            onClick={() => void handleResetMfa(row)}
                          >
                            Reset 2-step
                          </button>
                        )}
                        <button
                          type="button"
                          className="spark-btn-link"
                          disabled={busyId === row.id || row.id === currentUser?.id}
                          onClick={() => void handleToggleStatus(row)}
                        >
                          {row.status === 'active' ? 'Disable' : 'Enable'}
                        </button>
                        {isRoot && (
                          <button
                            type="button"
                            className="spark-btn-link"
                            disabled={busyId === row.id}
                            onClick={() => void handleToggleTicketing(row)}
                          >
                            {row.ticketing ? 'Revoke ticketing' : 'Allow ticketing'}
                          </button>
                        )}
                        {isRoot && (
                          <button
                            type="button"
                            className="spark-btn-link"
                            style={{ color: 'var(--sedl-red-70)' }}
                            disabled={busyId === row.id || row.id === currentUser?.id}
                            onClick={() => void handleDelete(row)}
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialogOpen && (
        <CreateUserDialog
          assignableRoles={assignableRoles}
          defaultPcc={currentUser?.pcc ?? ''}
          canGrantTicketing={isRoot}
          onClose={() => setDialogOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </>
  );
}

function formatDate(value?: string): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
